import jsPDF from 'jspdf';
import 'jspdf-autotable';

const BasePDF = ({ 
  type = 'invoice', // 'invoice', 'quote' ou 'schedule'
  invoice,
  quote, // Pour les devis
  client, 
  emitter = {
    name: "Matthieu SIMAR",
    address: "2550 Lignerolles",
    city: "27160 Breteuil-sur-Iton",
    email: "matthieu.simar@gmail.com",
    siret: "753 456 102 00023",
    siren: "753 456 102"
  },
  showPaymentInfo = true,
  percentages = [30, 40, 30], // Pour les échéanciers
  scheduleData = null, // Données spécifiques aux échéanciers
  validityDays = 30 // Pour les devis
}) => {
  
  // Fonction helper pour convertir le prix de manière sûre
  const parsePrice = (price) => {
    if (typeof price === 'number') return price;
    if (typeof price === 'string') return parseFloat(price.replace(',', '.'));
    return 0;
  };

  // Utiliser les bonnes données selon le type
  const data = type === 'quote' ? quote : invoice;
  
  // Calcul des totaux
  const totalHT = data.products.reduce((acc, item) => 
    acc + item.quantity * parsePrice(item.unitPrice), 0
  );
  
  // Calcul des remises (produits avec prix négatif) - pour factures et devis
  const totalRemise = (type === 'invoice' || type === 'quote') ? data.products.reduce((acc, item) => {
    const price = parsePrice(item.unitPrice);
    return price < 0 ? acc + Math.abs(item.quantity * price) : acc;
  }, 0) : 0;

  // Génération des échéances pour les échéanciers
  const generateSchedule = () => {
    if (type !== 'schedule') return [];
    
    const schedule = [];
    const today = new Date();
    
    percentages.forEach((percentage, index) => {
      const date = new Date(today);
      date.setDate(today.getDate() + (index * 30)); // Échéance tous les 30 jours
      
      const amount = (totalHT * percentage) / 100;
      
      schedule.push({
        num: index + 1,
        date: date,
        description: `Échéance ${index + 1} - ${percentage}% du montant total`,
        amount: amount,
        status: index === 0 ? 'En attente' : 'À venir'
      });
    });
    
    return schedule;
  };

  // Fonction principale pour générer le PDF
  const generatePDF = () => {
    // Créer une nouvelle instance de jsPDF
    const doc = new jsPDF();
    
    // Configuration des marges et dimensions (reproduction exacte du web)
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20; // Marge comme dans le CSS
    const innerPadding = 10; // Padding interne pour éviter le chevauchement avec la bordure
    
    // Variables pour le contenu
    let currentY = margin;
    
    // Couleurs exactes du CSS
    const colors = {
      emerald: [46, 175, 111],    // #2eaf6f
      navy: [35, 61, 90],         // #233d5a
      accent: [194, 29, 29],      // #c21d1d
      grey1: [51, 51, 51],        // #333
      grey2: [102, 102, 102],     // #666
      greyBg: [247, 247, 247],    // #f7f7f7
      tableBorder: [226, 226, 226] // #e2e2e2
    };
    
    // Fonction pour dessiner la bordure fine comme dans le CSS
    const drawBorder = () => {
      doc.setDrawColor(...colors.emerald);
      doc.setLineWidth(0.5); // Bordure fine de 2px CSS = 0.5 en jsPDF
      doc.roundedRect(
        margin, 
        margin, 
        pageWidth - (margin * 2), 
        pageHeight - (margin * 2), 
        3, 
        3
      );
    };
    
    // Fonction pour ajouter seulement la pagination (footer minimal)
    const addFooter = (totalPages) => {
      const footerY = pageHeight - margin - 10;
      
      // Pagination dynamique
      const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal'); // Police normale comme sur le web
      doc.setTextColor(...colors.grey2);
      doc.text(`Page ${pageNumber} / ${totalPages}`, pageWidth - margin - 25, footerY);
    };
    
    // Fonction utilitaire pour vérifier l'espace et gérer les sauts de page
    const checkSpaceAndAddPageIfNeeded = (requiredHeight, includeFooterSpace = true) => {
      const footerSpace = includeFooterSpace ? 45 : 0; // Réduit de 75 à 45px
      const maxYAllowed = pageHeight - margin - footerSpace;
      
      if (currentY + requiredHeight > maxYAllowed) {
        doc.addPage();
        drawBorder();
        currentY = margin + innerPadding;
        return true; // Indique qu'une nouvelle page a été créée
      }
      return false; // Pas de nouvelle page nécessaire
    };
    
    // Dessiner la bordure fine sur chaque page
    drawBorder();
    
    // DÉBUT DU CONTENU (à l'intérieur de la bordure)
    currentY = margin + 8;
    
    // ===== EN-TÊTE COMPACT (logo + montant total) =====
    // Logo réel (tentative de chargement)
    try {
      // Si le logo existe, on l'utilise, sinon on fait un placeholder simple
      doc.addImage('/logoMS.png', 'PNG', margin + innerPadding, currentY, 15, 15);
    } catch (error) {
      // Placeholder simple et discret si le logo ne charge pas
      doc.setFillColor(200, 200, 200);
      doc.roundedRect(margin + innerPadding, currentY, 15, 15, 2, 2, 'F');
      doc.setFontSize(6);
      doc.setTextColor(120, 120, 120);
      doc.text('MS', margin + innerPadding + 5, currentY + 9);
    }
    
    // Montant total à droite (style web exact)
    doc.setFontSize(10);
    doc.setTextColor(...colors.grey2);
    doc.setFont('helvetica', 'normal');
    doc.text('Montant total', pageWidth - margin - 40, currentY + 5);
    
    doc.setFontSize(16);
    doc.setFont('helvetica', 'normal'); // Moins gras comme sur le web
    doc.setTextColor(...colors.grey1);
    doc.text(`${totalHT.toFixed(2)} €`, pageWidth - margin - 40, currentY + 12);
    
    currentY += 20;
    
    // Ligne de séparation émeraude (style web)
    doc.setDrawColor(...colors.emerald);
    doc.setLineWidth(0.5);
    doc.line(margin + innerPadding, currentY, pageWidth - margin - innerPadding, currentY);
    currentY += 8;
    
    // ===== TITRE DYNAMIQUE + NUMÉRO =====
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold'); // Titre en gras pour la hiérarchie
    doc.setTextColor(...colors.grey1);
    const title = type === 'invoice' ? 'FACTURE' : type === 'quote' ? 'DEVIS' : 'ÉCHÉANCIER DE PAIEMENT';
    doc.text(title, margin + innerPadding, currentY);
    
    // Numéro aligné complètement à droite (comme sur le web)
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...colors.grey2);
    const numberText = type === 'quote' ? `N° ${data.num}` : type === 'invoice' ? `N° ${data.num}` : `Facture N° ${data.num}`;
    const numberTextWidth = doc.getTextWidth(numberText);
    doc.text(numberText, pageWidth - margin - innerPadding - numberTextWidth, currentY);
    currentY += 5; // Encore plus réduit pour rapprocher davantage
    
    // ===== BLOCS ÉMETTEUR ET CLIENT =====
    const colWidth = (pageWidth - margin * 2 - 40) / 2;
    
    // Ajouter un espacement après le titre FACTURE
    currentY += 8;
    
    // Détails émetteur en premier
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold'); // Nom de l'émetteur en gras
    doc.setTextColor(...colors.grey1);
    doc.text(emitter.name, margin + innerPadding, currentY);
    
    // Label destinataire légèrement décalé vers le bas
    const clientLabelY = currentY + 2; // Petit décalage vers le bas
    doc.setFontSize(9); // Même taille que l'émetteur
    doc.setFont('helvetica', 'bold'); // Label en gras
    doc.setTextColor(...colors.grey1);
    doc.text('À L\'ATTENTION DE', margin + innerPadding + colWidth + 20, clientLabelY);
    
    // Continuer les détails émetteur
    doc.setFontSize(9); // S'assurer de la taille uniforme
    doc.setFont('helvetica', 'normal'); // Retour en normal pour les détails
    let emitterY = currentY + 4;
    doc.text(emitter.address, margin + innerPadding, emitterY);
    emitterY += 4;
    doc.text(emitter.city, margin + innerPadding, emitterY);
    emitterY += 4;
    doc.text(emitter.email, margin + innerPadding, emitterY);
    emitterY += 4;
    doc.text(`SIRET : ${emitter.siret}`, margin + innerPadding, emitterY);
    emitterY += 4;
    doc.text(`SIREN : ${emitter.siren}`, margin + innerPadding, emitterY);
    
    // Détails client (commencer après le label avec espacement)
    let clientY = clientLabelY + 6; // Retour chariot après "À L'ATTENTION DE"
    doc.setFontSize(9); // Même taille que l'émetteur
    doc.setFont('helvetica', 'bold'); // Nom du client en gras
    doc.text(client.Title, margin + innerPadding + colWidth + 20, clientY);
    
    clientY += 4;
    doc.setFontSize(9); // S'assurer de la taille uniforme
    doc.setFont('helvetica', 'normal'); // Détails du client en normal
    doc.text(client.Adress, margin + innerPadding + colWidth + 20, clientY);
    clientY += 4;
    doc.text(`${client.PostalCode} ${client.Ville}`, margin + innerPadding + colWidth + 20, clientY);
    
    currentY = Math.max(emitterY, clientY) + 12; // Plus d'espace avant la date
    
    // ===== DATE DYNAMIQUE =====
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal'); // Police normale comme sur le web
    doc.setTextColor(...colors.grey1);
    
    if (type === 'invoice') {
      doc.setFont('helvetica', 'bold'); // Label en gras pour la hiérarchie
      doc.text('Date de la prestation : ', margin + innerPadding, currentY);
      doc.setFont('helvetica', 'normal'); // Valeur en normal
      const prestaDate = new Date(data.prestaDate.seconds * 1000).toLocaleDateString('fr-FR', { 
        month: 'long', 
        year: 'numeric' 
      });
      doc.text(prestaDate, margin + innerPadding + 45, currentY);
    } else if (type === 'quote') {
      doc.setFont('helvetica', 'bold'); // Label en gras pour la hiérarchie
      doc.text('Date de création : ', margin + innerPadding, currentY);
      doc.setFont('helvetica', 'normal'); // Valeur en normal
      const createDate = new Date().toLocaleDateString('fr-FR', { 
        day: 'numeric',
        month: 'long', 
        year: 'numeric' 
      });
      doc.text(createDate, margin + innerPadding + 35, currentY);
    } else {
      doc.setFont('helvetica', 'bold'); // Label en gras pour la hiérarchie
      doc.text('Date de création de l\'échéancier : ', margin + innerPadding, currentY);
      doc.setFont('helvetica', 'normal'); // Valeur en normal
      const dateText = new Date().toLocaleDateString('fr-FR', { 
        day: 'numeric',
        month: 'long', 
        year: 'numeric' 
      });
      doc.text(dateText, margin + innerPadding + 55, currentY);
    }
    currentY += 10; // Réduit de 15 à 10 pour rapprocher du tableau
    
    // ===== TABLEAU DYNAMIQUE =====
    const availableWidth = pageWidth - (margin * 2) - (innerPadding * 2); // Marge interne des deux côtés
    
    let tableData, tableHeaders, columnStyles;
    
    if (type === 'invoice' || type === 'quote') {
      // Tableau des articles pour factures et devis avec séparation titre/description
      tableData = [];
      data.products.forEach((item, index) => {
        const itemTotal = item.quantity * parsePrice(item.unitPrice);
        
        // Ligne principale avec le titre
        tableData.push([
          (index + 1).toString(),
          item.title,
          item.quantity.toString(),
          `${parsePrice(item.unitPrice).toFixed(2)} €`,
          `${itemTotal.toFixed(2)} €`,
          `${itemTotal.toFixed(2)} €`
        ]);
        
        // Ligne de description si elle existe
        if (item.description && item.description.trim()) {
          tableData.push([
            '', // Numéro vide
            item.description,
            '', // Quantité vide
            '', // Prix vide
            '', // Montant HT vide
            ''  // Montant TTC vide
          ]);
        }
      });
      
      tableHeaders = [['N°', 'Prestation/produit', 'Quantité', 'Prix HT', 'Montant HT', 'Montant TTC']];
      columnStyles = {
        0: { halign: 'center', cellWidth: availableWidth * 0.08 }, // 8%
        1: { cellWidth: availableWidth * 0.40 }, // 40%
        2: { halign: 'center', cellWidth: availableWidth * 0.12 }, // 12%
        3: { halign: 'right', cellWidth: availableWidth * 0.15 }, // 15%
        4: { halign: 'right', cellWidth: availableWidth * 0.12 }, // 12%
        5: { halign: 'right', cellWidth: availableWidth * 0.13 } // 13%
      };
    } else {
      // Tableau des échéances basé sur les factures créées
      tableData = data.products.map((item, index) => [
        (index + 1).toString(),
        item.title,
        item.description || '',
        `${parsePrice(item.unitPrice).toFixed(2)} €`
      ]);

      tableHeaders = [['N°', 'Mois', 'Description', 'Montant']];
      columnStyles = {
        0: { halign: 'center', cellWidth: availableWidth * 0.10 },
        1: { cellWidth: availableWidth * 0.45 },
        2: { cellWidth: availableWidth * 0.30 },
        3: { halign: 'right', cellWidth: availableWidth * 0.15 }
      };
    }
    
    doc.autoTable({
      head: tableHeaders,
      body: tableData,
      startY: currentY,
      margin: { left: margin + innerPadding, right: margin + innerPadding },
      tableWidth: availableWidth,
      headStyles: {
        fillColor: colors.navy,
        textColor: [255, 255, 255],
        fontSize: 8,
        fontStyle: 'bold',
        halign: 'left'
      },
      bodyStyles: {
        fontSize: 8,
        textColor: colors.grey1,
        fillColor: colors.greyBg,
        cellPadding: { top: 2, right: 3, bottom: 2, left: 3 }, // Padding réduit par défaut
        lineWidth: 0
      },
      alternateRowStyles: {
        fillColor: colors.greyBg
      },
      columnStyles: columnStyles,
      didParseCell: function(data) {
        // Pour les factures et devis seulement
        if ((type === 'invoice' || type === 'quote') && data.section === 'body') {
          const rowData = tableData[data.row.index];
          
          if (rowData) {
            // Si la première colonne est vide, c'est une ligne de description
            if (rowData[0] === '' && data.column.index === 1) {
              // Ligne de description : italique et couleur grise
              data.cell.styles.fontStyle = 'italic';
              data.cell.styles.textColor = colors.grey2;
              // Réduire drastiquement l'espacement pour les descriptions
              data.cell.styles.cellPadding = { top: 0, right: 3, bottom: 1, left: 3 };
              data.cell.styles.minCellHeight = 8; // Hauteur minimale réduite
            }
            // Si la première colonne contient un numéro, c'est une ligne de titre
            else if (rowData[0] !== '' && data.column.index === 1) {
              // Ligne de titre : gras
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.textColor = colors.grey1;
              // Réduire le padding bas du titre pour rapprocher la description
              data.cell.styles.cellPadding = { top: 3, right: 3, bottom: 0, left: 3 };
            }
            // Pour toutes les autres colonnes des lignes de description, réduire aussi l'espacement
            else if (rowData[0] === '') {
              data.cell.styles.cellPadding = { top: 0, right: 3, bottom: 1, left: 3 };
              data.cell.styles.minCellHeight = 8;
            }
          }
        }
      },
      didDrawPage: function(data) {
        // Redessiner la bordure fine sur chaque nouvelle page
        drawBorder();
      }
    });
    
    // Position après le tableau
    currentY = doc.lastAutoTable.finalY + 15;
    
    // ===== BLOC TOTAUX (adapté selon le type) =====
    // Vérifier l'espace pour la section totaux (hauteur estimée : 25px) - sans réserver l'espace footer
    checkSpaceAndAddPageIfNeeded(25, false);
    
    const totalsX = pageWidth - margin - innerPadding;
    const totalsWidth = 85; // Augmenté encore plus pour un trait plus long
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...colors.grey1);
    
    if (type === 'invoice' || type === 'quote') {
      // Totaux pour factures et devis
      doc.text('Total HT', totalsX - totalsWidth, currentY);
      doc.text(`${totalHT.toFixed(2)} €`, totalsX - 20, currentY);
      
      // Ligne de séparation
      doc.setDrawColor(...colors.tableBorder);
      doc.setLineWidth(0.2);
      doc.line(totalsX - totalsWidth, currentY + 2, totalsX - innerPadding, currentY + 2);
      currentY += 8;
      
      // Total remise si applicable
      if (totalRemise > 0) {
        doc.text('Total remise', totalsX - totalsWidth, currentY);
        doc.text(`${totalRemise.toFixed(2)} €`, totalsX - 20, currentY);
        doc.line(totalsX - totalsWidth, currentY + 2, totalsX - innerPadding, currentY + 2);
        currentY += 8;
      }
      
      // Total TTC (style accent) - trait noir plus long
      doc.setDrawColor(...colors.grey1);
      doc.setLineWidth(0.3); // Plus fin comme sur le web
      doc.line(totalsX - totalsWidth, currentY - 2, totalsX - 5, currentY - 2); // Plus long jusqu'au bord
      
      doc.setFont('helvetica', 'normal'); // Police normale comme sur le web
      doc.setFontSize(12);
      doc.text('Total TTC', totalsX - totalsWidth, currentY + 3);
      doc.setTextColor(...colors.accent);
      doc.text(`${totalHT.toFixed(2)} €`, totalsX - 20, currentY + 3);
      doc.setTextColor(...colors.grey1);
    } else {
      // Totaux pour échéanciers
      doc.text('Montant total de la facture', totalsX - totalsWidth, currentY);
      doc.text(`${totalHT.toFixed(2)} €`, totalsX - 20, currentY);
      
      // Ligne de séparation
      doc.setDrawColor(...colors.tableBorder);
      doc.setLineWidth(0.2);
      doc.line(totalsX - totalsWidth, currentY + 2, totalsX - innerPadding, currentY + 2);
      currentY += 8;
      
      // Montant déjà payé
      doc.text('Montant déjà payé', totalsX - totalsWidth, currentY);
      doc.text('0,00 €', totalsX - 20, currentY);
      doc.line(totalsX - totalsWidth, currentY + 2, totalsX - innerPadding, currentY + 2);
      currentY += 8;
      
      // Montant restant dû (style accent)
      doc.setDrawColor(...colors.grey1);
      doc.setLineWidth(0.5);
      doc.line(totalsX - totalsWidth, currentY - 2, totalsX - innerPadding, currentY - 2);
      
      doc.setFont('helvetica', 'normal'); // Police normale comme sur le web
      doc.setFontSize(12);
      doc.text('Montant restant dû', totalsX - totalsWidth, currentY + 3);
      doc.setTextColor(...colors.accent);
      doc.text(`${totalHT.toFixed(2)} €`, totalsX - 20, currentY + 3);
      doc.setTextColor(...colors.grey1);
    }
    currentY += 20;
    
    // ===== SECTION VALIDITÉ POUR LES DEVIS =====
    if (type === 'quote') {
      // Vérifier l'espace disponible pour la section validité (hauteur estimée : 25px) - sans réserver l'espace footer
      checkSpaceAndAddPageIfNeeded(25, false);
      
      // Calcul de la date de validité
      const validityDate = new Date();
      validityDate.setDate(validityDate.getDate() + validityDays);
      
      // Section validité avec background gris
      doc.setFillColor(...colors.greyBg);
      doc.roundedRect(margin + innerPadding, currentY, pageWidth - margin * 2 - (innerPadding * 2), 15, 3, 3, 'F');
      
      currentY += 8;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...colors.grey2);
      
      const validityText = `Devis valable jusqu'au ${validityDate.toLocaleDateString('fr-FR', { 
        day: 'numeric',
        month: 'long', 
        year: 'numeric' 
      })}`;
      const validityTextWidth = doc.getTextWidth(validityText);
      const startX = (pageWidth - validityTextWidth) / 2;
      
      doc.text(validityText, startX, currentY);
      currentY += 20;
    }
    
    // ===== VÉRIFICATION D'ESPACE POUR FOOTER =====
    // Calculer l'espace nécessaire pour le footer (paiement + mentions légales + contact)
    const footerHeight = type === 'invoice' ? 35 : type === 'quote' ? 50 : 60; // Hauteur estimée selon le type réduite
    
    // Vérifier si on a assez d'espace pour le footer complet
    checkSpaceAndAddPageIfNeeded(footerHeight, false);
    
    // ===== SECTION PAIEMENT =====
    if (showPaymentInfo && type !== 'quote') { // Pas de paiement pour les devis
      if (type === 'invoice') {
        // Vérifier l'espace pour la section paiement simple (hauteur : 35px)
        checkSpaceAndAddPageIfNeeded(35, false);
        
        // Section paiement simple pour factures
        doc.setFillColor(...colors.greyBg);
        doc.roundedRect(margin + innerPadding, currentY, pageWidth - margin * 2 - (innerPadding * 2), 15, 3, 3, 'F');
        
        currentY += 8;
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...colors.grey2);
        
        // Texte de paiement simple sans pictogramme
        const paymentText = 'Paiement par virement – IBAN : FR76 4061 8804 4200 0409 1149 353';
        const textWidth = doc.getTextWidth(paymentText);
        const startX = (pageWidth - textWidth) / 2;
        
        doc.text(paymentText, startX, currentY);
        currentY += 20;
      } else {
        // Vérifier l'espace pour la section paiement détaillée (hauteur : 45px)
        checkSpaceAndAddPageIfNeeded(45, false);
        
        // Section paiement détaillée pour échéanciers
        doc.setFillColor(...colors.greyBg);
        doc.roundedRect(margin + innerPadding, currentY, pageWidth - margin * 2 - (innerPadding * 2), 25, 3, 3, 'F');
        
        currentY += 8;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal'); // Police normale comme sur le web
        doc.setTextColor(...colors.grey1);
        doc.text('Modalités de paiement', margin + innerPadding + 5, currentY);
        currentY += 6;
        
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...colors.grey2);
        doc.text('• Paiement par virement bancaire uniquement', margin + innerPadding + 5, currentY);
        currentY += 4;
        
        // IBAN avec pictogramme et police réduite
        const bulletText = '• ';
        const bulletWidth = doc.getTextWidth(bulletText);
        
        doc.text(bulletText, margin + innerPadding + 5, currentY);
        doc.setTextColor(...colors.emerald);
        doc.text('-', margin + innerPadding + 5 + bulletWidth, currentY);
        doc.setFontSize(7);
        doc.setTextColor(...colors.grey2);
        doc.text(' IBAN : FR76 4061 8804 4200 0409 1149 353', margin + innerPadding + 5 + bulletWidth + 2, currentY); // +2 pour l'espace du tiret
        doc.setFontSize(8); // Remettre la taille normale
        currentY += 4;
        doc.text(`• Merci de préciser le numéro de facture ${invoice.num} en objet du virement`, margin + innerPadding + 5, currentY);
        currentY += 4;
        doc.text('• En cas de retard de paiement, des pénalités pourront être appliquées', margin + innerPadding + 5, currentY);
        currentY += 15;
      }
    }
    
    // ===== MENTIONS LÉGALES =====
    // Vérifier l'espace pour les mentions légales (hauteur : 20px)
    checkSpaceAndAddPageIfNeeded(20, false);
    
    doc.setFontSize(8);
    doc.setTextColor(...colors.grey2);
    doc.setFont('helvetica', 'italic');
    doc.text('Dispensé d\'immatriculation au registre du commerce et des sociétés (RCS) et au répertoire des métiers (RM)', margin + innerPadding, currentY);
    currentY += 4;
    doc.text('TVA non applicable, art. 293 B du CGI', margin + innerPadding, currentY);
    currentY += 8;
    
    // ===== INFORMATIONS DE CONTACT =====
    // Vérifier l'espace pour les informations de contact (hauteur : 20px)
    checkSpaceAndAddPageIfNeeded(20, false);
    
    // Ligne de séparation fine
    doc.setDrawColor(...colors.tableBorder);
    doc.setLineWidth(0.2);
    doc.line(margin + innerPadding, currentY, pageWidth - margin - innerPadding, currentY);
    currentY += 8;
    
    // Informations de contact (style web)
    doc.setFontSize(8);
    doc.setTextColor(...colors.grey2);
    doc.setFont('helvetica', 'normal');
    const contactText = type === 'invoice' 
      ? `Pour toute question concernant cette facture, contactez-nous à ${emitter.email}`
      : type === 'quote'
      ? `Pour toute question concernant ce devis, contactez-nous à ${emitter.email}`
      : `Pour toute question concernant cet échéancier, contactez-nous à ${emitter.email}`;
    doc.text(contactText, margin + innerPadding, currentY);
    
    // Ajouter le footer sur toutes les pages
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      addFooter(totalPages); // Ajouter le footer sur chaque page avec le nombre total
    }
    
    return doc;
  };

  // Méthode pour télécharger le PDF
  const downloadPDF = () => {
    const doc = generatePDF();
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    if (type === 'invoice') {
      doc.save(`${date}-F${data.num}-${client.Title.replace(/\s+/g, '')}.pdf`);
    } else if (type === 'quote') {
      doc.save(`${date}-D${data.num}-${client.Title.replace(/\s+/g, '')}.pdf`);
    } else {
      doc.save(`Echéancier_${data.num}.pdf`);
    }
  };

  // Méthode pour ouvrir le PDF dans un nouvel onglet
  const openPDF = () => {
    const doc = generatePDF();
    const pdfDataUri = doc.output('datauristring');
    const newWindow = window.open();
    newWindow.document.write(`<iframe width='100%' height='100%' src='${pdfDataUri}'></iframe>`);
  };

  // Retourner les méthodes utiles
  return {
    downloadPDF,
    openPDF,
    generatePDF
  };
};

export default BasePDF; 